import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Paperclip, ShieldWarning } from "@phosphor-icons/react";
import { openHiringFile } from "@/pages/admin/hiring/openHiringFile";

export default function ScreeningPanel({ id, meta, stage, onChanged }) {
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState({});
  const [busy, setBusy] = useState(false);
  const [decision, setDecision] = useState({ decision: "pending", reason: "" });

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/hiring/applications/${id}/screening`);
      setData(d);
      if (d.record?.eligibility) setDecision({ decision: d.record.eligibility.decision || "pending", reason: "" });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); await load(); onChanged(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (!data) return <p className="text-sm text-muted-foreground">Loading screening record…</p>;

  const rec = data.record;
  const signed = (rec?.disclosures || []).filter((d) => d.signed_at);

  return (
    <section className="rounded-2xl border-2 border-amber-300 bg-amber-50/40 p-5" data-testid="screening-panel">
      <h3 className="inline-flex items-center gap-2 font-bold">
        <ShieldWarning size={20} className="text-amber-600" weight="fill" /> Background screening (restricted)
      </h3>
      <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
        This website does not run any background check. Record here the status of the checks carried out through the
        official Arkansas and federal processes, and where the result is held.
      </p>

      {!rec ? (
        <div className="mt-4">
          {data.can_start && meta.can.screening_edit ? (
            <Button disabled={busy} className="rounded-full font-bold bg-[#3B82F6]" data-testid="start-screening-btn"
                    onClick={() => act(() => api.post(`/hiring/applications/${id}/screening/start`), "Screening record opened")}>
              Open the screening record
            </Button>
          ) : (
            <p className="text-sm font-semibold" data-testid="screening-not-available">
              Screening starts once a conditional offer has been recorded. Current stage: {stage || "not submitted"}.
            </p>
          )}
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          <div className="rounded-xl bg-white border border-border p-4 text-sm">
            <p className="font-bold">Eligibility: <span data-testid="eligibility-value">{rec.eligibility?.decision || "pending"}</span></p>
            {rec.eligibility?.at && <p className="text-muted-foreground">{rec.eligibility.by_name || rec.eligibility.by} · {new Date(rec.eligibility.at).toLocaleString()}</p>}
            {rec.eligibility?.reason && <p className="mt-1 italic">{rec.eligibility.reason}</p>}
            <p className="mt-2 text-muted-foreground">
              Signed authorizations: {signed.length}{rec.dob ? " · date of birth on file for identity matching" : ""}
            </p>
          </div>

          {meta.can.screening_edit && (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="rounded-full font-bold" disabled={busy}
                      data-testid="send-disclosure-btn"
                      onClick={() => act(() => api.post(`/hiring/applications/${id}/screening/disclosure`),
                        "Disclosure and authorization sent to the applicant")}>
                Send disclosure and authorization
              </Button>
              <label className="inline-flex items-center gap-2 text-sm font-bold text-[#3B82F6] cursor-pointer">
                <Paperclip size={16} /> Store restricted evidence
                <input type="file" className="hidden" data-testid="screening-evidence-upload"
                       onChange={(e) => {
                         const file = e.target.files?.[0];
                         if (!file) return;
                         const fd = new FormData();
                         fd.append("file", file);
                         fd.append("kind", "screening_evidence");
                         fd.append("label", file.name);
                         act(() => api.post(`/hiring/applications/${id}/screening/evidence`, fd), "Document stored");
                       }} />
              </label>
            </div>
          )}

          {(data.documents || []).length > 0 && (
            <div className="text-sm" data-testid="screening-documents">
              <p className="font-bold">Stored documents</p>
              {data.documents.map((d) => (
                <Button key={d.id} variant="link" className="px-0 block" data-testid={`screening-doc-${d.id}`}
                        onClick={() => openHiringFile(d.id, d.filename)}>
                  v{d.version} · {d.filename} · {new Date(d.created_at).toLocaleDateString()}
                </Button>
              ))}
            </div>
          )}

          <div className="space-y-3" data-testid="screening-components">
            {(rec.components || []).map((c) => {
              const draft = edit[c.key] || {};
              return (
                <div key={c.key} className="rounded-xl bg-white border border-border p-4">
                  <div className="flex flex-wrap justify-between gap-2">
                    <div>
                      <p className="font-bold">{c.label}{c.required ? " *" : ""}</p>
                      <p className="text-xs text-muted-foreground">{c.authority}</p>
                    </div>
                    <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-slate-100"
                          data-testid={`component-status-${c.key}`}>{data.statuses.find((s) => s.key === c.status)?.label || c.status}</span>
                  </div>
                  {c.completed_at && <p className="mt-1 text-xs text-muted-foreground">Completed {c.completed_at} · held at: {c.evidence_ref}</p>}
                  {meta.can.screening_edit && (
                    <div className="mt-3 grid gap-2 sm:grid-cols-4">
                      <Select value={draft.status || c.status}
                              onValueChange={(v) => setEdit({ ...edit, [c.key]: { ...draft, status: v } })}>
                        <SelectTrigger data-testid={`component-select-${c.key}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{data.statuses.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input type="date" value={draft.requested_at ?? c.requested_at} placeholder="Requested"
                             data-testid={`component-requested-${c.key}`}
                             onChange={(e) => setEdit({ ...edit, [c.key]: { ...draft, requested_at: e.target.value } })} />
                      <Input type="date" value={draft.completed_at ?? c.completed_at} placeholder="Completed"
                             data-testid={`component-completed-${c.key}`}
                             onChange={(e) => setEdit({ ...edit, [c.key]: { ...draft, completed_at: e.target.value } })} />
                      <Input value={draft.evidence_ref ?? c.evidence_ref} placeholder="Where the result is held"
                             data-testid={`component-evidence-${c.key}`}
                             onChange={(e) => setEdit({ ...edit, [c.key]: { ...draft, evidence_ref: e.target.value } })} />
                      <Button size="sm" disabled={busy} className="rounded-full font-bold sm:col-span-4 justify-self-start"
                              data-testid={`component-save-${c.key}`}
                              onClick={() => act(() => api.post(`/hiring/applications/${id}/screening/components/${c.key}`, {
                                status: draft.status || c.status,
                                requested_at: draft.requested_at ?? c.requested_at ?? "",
                                completed_at: draft.completed_at ?? c.completed_at ?? "",
                                expires_at: c.expires_at || "",
                                evidence_ref: draft.evidence_ref ?? c.evidence_ref ?? "",
                                notes: c.notes || "" }), "Component updated")}>
                        Save this check
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {meta.can.screening_edit && (
            <div className="rounded-xl bg-white border border-border p-4" data-testid="adverse-action-block">
              <p className="font-bold">If a report may affect the decision</p>
              <p className="text-sm text-muted-foreground mt-1">
                Send the pre-adverse notice with a copy of the report and the summary of rights, allow time to
                dispute, then record the final notice. Nobody is rejected automatically.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {[["pre_adverse_notice", "Record pre-adverse notice"], ["copy_provided", "Copy of report provided"],
                  ["final_adverse_notice", "Record final notice"]].map(([step, label]) => (
                  <Button key={step} size="sm" variant="outline" className="rounded-full font-bold" disabled={busy}
                          data-testid={`adverse-${step}`}
                          onClick={() => act(() => api.post(`/hiring/applications/${id}/screening/adverse-action`,
                            { step, notes: window.prompt("Note for the record? (optional)") || "" }), "Recorded")}>
                    {label}
                  </Button>
                ))}
              </div>
              {rec.adverse_action?.pre_adverse_notice_at && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Pre-adverse {new Date(rec.adverse_action.pre_adverse_notice_at).toLocaleString()}
                  {rec.adverse_action.copy_provided_at ? ` · copy provided ${new Date(rec.adverse_action.copy_provided_at).toLocaleString()}` : ""}
                  {rec.adverse_action.final_adverse_notice_at ? ` · final ${new Date(rec.adverse_action.final_adverse_notice_at).toLocaleString()}` : ""}
                </p>
              )}
            </div>
          )}

          {meta.can.screening_decide && (
            <div className="rounded-xl bg-white border border-border p-4" data-testid="eligibility-block">
              <p className="font-bold">Record the eligibility decision</p>
              <div className="mt-3 grid gap-2 sm:grid-cols-3">
                <Select value={decision.decision} onValueChange={(v) => setDecision({ ...decision, decision: v })}>
                  <SelectTrigger data-testid="eligibility-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="eligible">Eligible to work with children</SelectItem>
                    <SelectItem value="not_eligible">Not eligible</SelectItem>
                  </SelectContent>
                </Select>
                <Textarea className="sm:col-span-2 min-h-16" value={decision.reason} placeholder="Reason for this decision (required)"
                          data-testid="eligibility-reason" onChange={(e) => setDecision({ ...decision, reason: e.target.value })} />
              </div>
              <Button disabled={busy || !decision.reason.trim()} className="mt-3 rounded-full font-bold bg-[#3B82F6]"
                      data-testid="save-eligibility-btn"
                      onClick={() => act(() => api.post(`/hiring/applications/${id}/screening/eligibility`, decision), "Eligibility recorded")}>
                Save decision
              </Button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
